import { 
  IonModal, 
  IonHeader, 
  IonToolbar, 
  IonButtons, 
  IonButton, 
  IonIcon, 
  IonContent 
} from '@ionic/react';
import { close, printOutline, downloadOutline } from 'ionicons/icons';
import { useState, useEffect } from 'react';
import { TeamMember } from '../../types/teamSummary';
import { CourseList } from '../progress/CourseList';
import { progressService } from '../../services/progressService';
import { profileService } from '../../services/profileService';
import { Profile } from '../../types/profile';
import { formatLastLogin } from '../../utils/teamData';

interface UserCoursesModalProps {
  member: TeamMember;
  isOpen: boolean;
  onClose: () => void;
  onPrintDownload: () => void;
}

export function UserCoursesModal({ member, isOpen, onClose, onPrintDownload }: UserCoursesModalProps) {
  const [memberCourses, setMemberCourses] = useState<any[]>([]);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);

  useEffect(() => {
    if (isOpen && member?.id) {
      const fetchData = async () => {
        // Fetch courses
        const coursesData = await progressService.getTeamUserCourseSummary(member.id);
        setMemberCourses(coursesData);

        // Fetch profile information
        setIsLoadingProfile(true);
        try {
          const profileData = await profileService.getById(member.id);
          setProfile(profileData);
        } catch (error) {
          console.error('Failed to fetch profile:', error);
          setProfile(null);
        } finally {
          setIsLoadingProfile(false);
        }
      };
      fetchData();
    } else {
      // Reset state when modal closes
      setMemberCourses([]);
      setProfile(null);
    }
  }, [isOpen, member?.id]);

  return (
    <IonModal isOpen={isOpen} onDidDismiss={onClose}>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="end">
            {/* TODO: Uncomment when Print/Download feature is implemented
            <IonButton onClick={onPrintDownload} color="primary">
              <IonIcon icon={printOutline} slot="icon-only" />
            </IonButton>
            <IonButton onClick={onPrintDownload} color="primary">
              <IonIcon icon={downloadOutline} slot="icon-only" />
            </IonButton>
            */}
            <IonButton onClick={onClose}>
              <IonIcon icon={close} slot="icon-only" />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        {/* User Details Section */}
        <div className="bg-white rounded-lg shadow mb-4">
          <div
            className="border-b border-gray-200 px-6 py-4"
            style={{
              backgroundColor: 'var(--ion-color-secondary)',
              color: 'var(--ion-color-white)',
              borderTopLeftRadius: '12px',
              borderTopRightRadius: '12px'
            }}
          >
            <h2 className="text-2xl font-bold">{member.displayName}</h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-2 gap-x-8 gap-y-4">
              <div className="flex flex-col">
                <span className="text-gray-600 font-medium border-b border-gray-300 pb-0.5">First Name:</span>
                <span className="text-gray-800 mt-1">
                  {isLoadingProfile ? 'Loading...' : (profile?.firstName || 'N/A')}
                </span>
              </div>
              <div className="flex flex-col">
                <span className="text-gray-600 font-medium border-b border-gray-300 pb-0.5">Last Name:</span>
                <span className="text-gray-800 mt-1">
                  {isLoadingProfile ? 'Loading...' : (profile?.lastName || 'N/A')}
                </span>
              </div>
              <div className="flex flex-col">
                <span className="text-gray-600 font-medium border-b border-gray-300 pb-0.5">Email Address:</span>
                <span className="text-gray-800 mt-1 break-words">
                  {isLoadingProfile ? 'Loading...' : (profile?.email || 'N/A')}
                </span>
              </div>
              <div className="flex flex-col">
                <span className="text-gray-600 font-medium border-b border-gray-300 pb-0.5">Phone Number:</span>
                <span className="text-gray-800 mt-1">
                  {isLoadingProfile ? 'Loading...' : ((profile?.metadata?.settings as any)?.phone || 'N/A')}
                </span>
              </div>
              <div className="flex flex-col">
                <span className="text-gray-600 font-medium border-b border-gray-300 pb-0.5">Last Login:</span>
                <span className="text-gray-800 mt-1">{formatLastLogin(member.lastSeen)}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-gray-600 font-medium border-b border-gray-300 pb-0.5">Courses Completed:</span>
                <span className="text-gray-800 mt-1">{member.totalCoursesCompleted}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-gray-600 font-medium border-b border-gray-300 pb-0.5">Progress%:</span>
                <span className="text-gray-800 mt-1">{member.progress}%</span>
              </div>
              <div className="flex flex-col">
                <span className="text-gray-600 font-medium border-b border-gray-300 pb-0.5">Badges Earned:</span>
                <span className="text-gray-800 mt-1">{member.badges?.length || 0}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Courses Section */}
        <div className="bg-white rounded-lg shadow">
          <div
            className="border-b border-gray-200 px-6 py-4"
            style={{
              backgroundColor: 'var(--ion-color-secondary)',
              color: 'var(--ion-color-white)',
              borderTopLeftRadius: '12px',
              borderTopRightRadius: '12px'
            }}
          >
            <h2 className="text-2xl font-bold">Courses</h2>
          </div>
          <div className="p-6">
            {memberCourses.length === 0 ? (
              <p className="text-gray-600 text-center">{member.displayName} has not started coursework.</p>
            ) : (
              <CourseList courses={memberCourses} />
            )}
          </div>
        </div>
      </IonContent>
    </IonModal>
  );
}

